import { useEffect, useRef, useState, useCallback } from 'react';
import { useSimStore } from '../../store/useSimStore';
import MonitorRoute from '../monitor/MonitorRoute';
import ReplayBar from './ReplayBar';
import { parseCaseFile, interpolateAt, rhythmAt, ventAt } from '../../engine/caseReplay';
import styles from './ReplayRoute.module.css';

/**
 * /replay — load a saved case (JSON/CSV) and scrub through it. Drives the shared
 * store (vitals / rhythm / NIBP / channels) so the existing MonitorDisplay
 * renders the reconstructed state; a bottom bar provides play/pause, speed, a
 * scrubber, and ⏮/⏭ stepping between events and major vitals changes.
 *
 * NOTE: intended for standalone debrief use — if a live instructor tab is
 * publishing sync on the same device it would fight the replay writes.
 */
export default function ReplayRoute() {
  const [caseData, setCaseData] = useState(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const loadText = useCallback((text) => {
    try {
      const data = parseCaseFile(text);
      if (!data.samples.length && !data.events.length) { setError('No samples or events found in that file.'); return; }
      setError(null);
      setCaseData(data);
      setT(0);
      setPlaying(false);
      const s = useSimStore.getState();
      s.setAudioMuted(true); // silent during replay
      s.setChannelVisible('abp', data.vitalKeys.includes('ABP_Sys'));
      s.setChannelVisible('cvp', data.vitalKeys.includes('CVP'));
      s.setChannelVisible('etco2', data.vitalKeys.includes('EtCO2'));
      s.setChannelVisible('temp', data.vitalKeys.includes('Temp'));
    } catch (e) {
      setError('Could not parse file: ' + e.message);
    }
  }, []);

  // Handoff from the CaseRecorder "Replay" button (localStorage, cross-tab).
  useEffect(() => {
    try {
      const raw = localStorage.getItem('sim.replayCase');
      if (raw) { localStorage.removeItem('sim.replayCase'); loadText(raw); }
    } catch { /* ignore */ }
  }, [loadText]);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => loadText(String(r.result));
    r.readAsText(f);
    e.target.value = ''; // allow re-loading the same file
  };

  // Apply the reconstructed state at time t.
  useEffect(() => {
    if (!caseData) return;
    const s = useSimStore.getState();
    s.setReplayVitals(interpolateAt(caseData, t));
    const r = rhythmAt(caseData, t);
    s.applyRhythmSync(r, r, 1);
    // Ventilator screen + capnograph morphology (EtCO₂ detail).
    const { capno, split, intubated, vent, aid, media } = ventAt(caseData, t);
    if (capno) s.setCapnoMorphology(capno);
    s.setSplitScreen(split);
    s.setIntubated(intubated);
    if (vent) s.setVentSettings(vent);
    s.setActiveAidCard(aid);
    s.setActiveMedia(media);
  }, [caseData, t]);

  // Playback loop (real elapsed time × speed).
  useEffect(() => {
    if (!playing || !caseData) return;
    let raf, last = performance.now();
    const tick = (now) => {
      const dt = (now - last) * speed;
      last = now;
      setT((prev) => {
        const next = prev + dt;
        if (next >= caseData.durationMs) { setPlaying(false); return caseData.durationMs; }
        return next;
      });
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, speed, caseData]);

  const step = useCallback((dir) => {
    if (!caseData) return;
    setPlaying(false);
    const times = caseData.keyframes.map((k) => k.t);
    if (dir > 0) {
      const next = times.find((x) => x > t + 1);
      setT(next != null ? next : caseData.durationMs);
    } else {
      const prevs = times.filter((x) => x < t - 1);
      setT(prevs.length ? prevs[prevs.length - 1] : 0);
    }
  }, [caseData, t]);

  if (!caseData) {
    return (
      <div className={styles.loader}>
        <div className={styles.card}>
          <h1 className={styles.title}>Case Replay</h1>
          <p className={styles.sub}>Load a saved case recording (.json or .csv) exported from the Case Recorder.</p>
          <button className={styles.loadBtn} onClick={() => fileRef.current?.click()}>⤒ Load case file</button>
          <input ref={fileRef} type="file" accept=".json,.csv,application/json,text/csv" hidden onChange={onFile} />
          {error && <p className={styles.err}>{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.replayRoot}>
      <div className={styles.monitorWrap}><MonitorRoute /></div>
      <ReplayBar
        caseData={caseData}
        t={t}
        playing={playing}
        speed={speed}
        onToggle={() => setPlaying((p) => !p)}
        onSeek={(v) => { setPlaying(false); setT(v); }}
        onSpeed={setSpeed}
        onStep={step}
        onLoadNew={() => fileRef.current?.click()}
      />
      <input ref={fileRef} type="file" accept=".json,.csv,application/json,text/csv" hidden onChange={onFile} />
    </div>
  );
}
